import { HttpService } from '@ribajs/core'
import { normalizeUrl, justDigits, extend, handleize } from '@ribajs/utils'
import {
  setPageAndSearchParameters,
  isColor,
  extractNumber,
  responseSectionToJson,
  mergeShopifyProducts,
  getMarketId,
} from '../utils/index.js'
import pRetry, { AbortError } from 'p-retry'
import { cacheService } from './cache.service.js'
import { SbLocaleService } from './sb-locale.service.js'
import { ToasterService } from './toaster.service.js'
import {
  ANY_VARIANT_OPTION_IMAGE_REGEX_WITH_INDEX,
  MARKETS,
  DEFAULT_MULTIPACK,
  VALIDATE_QUANTITY_ERROR,
} from '../constants'

import type {
  ShopifyProduct,
  ShopifyProductVariant,
  Metafields,
  MetafieldsProductVariant,
  ShopifyProductVariantOption,
  ShopifyProductImage,
  ShopifyImage,
  ShopifyPreviewImage,
  ShopifyMedia,
  ShopifyCartLineItemFeaturedImage,
  ShopifyNormalizedImage,
  CardProductOptionValue,
  CollectionJsonResponse,
  CardProductOption,
  SbProductBadges,
  ShopifyMediaImage,
  SbProductVariantImageDetails,
  SbProductVariantImageDetailsGrouped,
  ProductVariantOptionImageRegex,
  Multipack,
  ShopifyProductVariantQuantityRule,
  ValidateQuantityResult,
  ShopifySellingPlan,
  ProductVariantOptionImagePattern,
} from '../types/index.js'

export interface MetafieldsCache {
  [url: string]: Metafields
}

export class ShopifyProductService {
  private static cache = cacheService.cache
  private static cacheExpiresIn = 1000 * 60 * 30 // 30 minutes

  // Used for market dependent visibility of "Neu" badge
  private static localizedNewBadgeTags: Record<string, string | undefined> = {
    DE: 'Kategorie: Neuheiten',
    FR: 'Kategorie: Nouveautés',
    INT: 'Kategorie: New', // TODO: check if this is correct
  }

  static root = window.Shopify?.routes?.root || '/'

  private static async _getSingle(
    handle: string,
    enableCache = true,
  ): Promise<ShopifyProduct> {
    if (!handle) {
      throw new TypeError(
        '[ShopifyProductService.getSingle] The product handle is required!',
      )
    }

    const cacheKey = this.getProductCacheKey(handle)

    if (enableCache) {
      const cached =
        await this.cache.resolve<Promise<ShopifyProduct | undefined>>(cacheKey)
      if (cached) {
        return cached
      }
    }

    // Get the product json object using the official shopify ajax api
    const res = await HttpService.getJSON<ShopifyProduct>(
      `${ShopifyProductService.root}products/${handle}.js`,
    )

    if (res.status >= 400) {
      throw new AbortError(res.statusText)
    }

    // Assign the original product data
    let product = res.body

    // Get the product json object using our own json product json helper snippet
    // Set view=raw to get the json object, see `shopify/layout/raw.liquid` for this
    // We use this to get missing properties in the original product json object from our own json product json helper snippet
    {
      let product2: ShopifyProduct | null = null
      const url = new URL(
        `${ShopifyProductService.root}products/${handle}`,
        window.location.href,
      )
      url.searchParams.set('view', 'json')

      try {
        product2 = await responseSectionToJson(
          await HttpService.get<string>(url.toString()),
        )
        // Merge the original product data with the missing properties
        if (product2) {
          product = mergeShopifyProducts(product, product2)
        }
      } catch (error) {
        console.error(error)
      }
    }

    this.cache.resolve<ShopifyProduct>(cacheKey, product, this.cacheExpiresIn)

    return product
  }

  public static getProductHandleFromUrl(url: string) {
    const parts = url.split('products/')
    const handle = parts[parts.length - 1].split('?')[0]
    return handle
  }

  public static getProductCacheKey(handle: string) {
    return `product-${handle}`
  }

  public static getMetafieldsCacheKey(productHandleOrUrl: string) {
    if (productHandleOrUrl.includes('products/')) {
      productHandleOrUrl = this.getProductHandleFromUrl(productHandleOrUrl)
    }
    return `product-metafields-${productHandleOrUrl}`
  }

  public static async cacheProduct(product: ShopifyProduct) {
    const cacheKey = this.getProductCacheKey(product.handle)
    return this.cache.resolve<ShopifyProduct>(
      cacheKey,
      product,
      this.cacheExpiresIn,
    )
  }

  public static cacheMetafields(
    metafields: Metafields,
    productHandleOrUrl: string,
  ) {
    const cacheKey = this.getMetafieldsCacheKey(productHandleOrUrl)
    return this.cache.resolve<Metafields>(
      cacheKey,
      metafields,
      this.cacheExpiresIn,
    )
  }

  /**
   * Check the product and its variants and remove variants and options that are not available in the current market.
   */
  private static cleanIrrelevantVariantsAndOptions(
    product: ShopifyProduct,
    allowedVariantIds?: number[],
  ) {
    // Remove variants that are not on the allowed list
    if (allowedVariantIds?.length) {
      product.variants = product.variants.filter(variant =>
        allowedVariantIds.includes(variant.id),
      )
    }

    // Remove variants that are hidden on the current market
    product.variants = product.variants.filter(variant => {
      if (this.hiddenOnMarket(variant.metafields)) {
        return false
      }

      return true
    })

    // Remove options that are not used by any (still) existing variant
    product.options.forEach(option => {
      option.values = option.values.filter(optionValue => {
        if (
          !product.variants.some(
            variant => variant[`option${option.position}`] === optionValue,
          )
        ) {
          return false
        }

        return true
      })
    })

    // Update available flag because we might have removed some variants
    product.available =
      product.variants.length > 0 &&
      product.variants.some(variant => variant.available)

    return product
  }

  public static async getSingle(
    handle: string,
    enableCache = true,
  ): Promise<ShopifyProduct> {
    return pRetry(this._getSingle.bind(this, handle, enableCache))
  }

  /**
   * Get products array by handles
   * @param handles The product handles
   */
  public static async get(
    handles: string | string[],
  ): Promise<ShopifyProduct[]> {
    if (typeof handles === 'string') {
      if (handles) {
        handles = [handles]
      } else {
        handles = []
      }
    }

    if (!Array.isArray(handles)) {
      throw new TypeError(
        '[ShopifyProductService.get] The product handles must be an array of product handles or a string of a single product handle!',
      )
    }

    const products: ShopifyProduct[] = []

    for await (const handle of handles) {
      const product = await this.getSingle(handle)
      products.push(product)
    }

    return products
  }

  public static getProductUrl(
    productHandle: string,
    withinCollectionHandle?: string,
    searchParameter?: URLSearchParams,
  ) {
    let _url = `/products/${productHandle}`

    if (withinCollectionHandle && !_url.includes('/collections/')) {
      _url = this.getCollectionUrl(withinCollectionHandle).pathname + _url
    }

    if (!_url?.startsWith('http')) {
      _url = window.location.protocol + '//' + window.location.host + _url
    }

    const url = new URL(_url)

    if (searchParameter) {
      searchParameter.forEach((value, key) => {
        url.searchParams.set(key, value)
      })
    }

    return url
  }

  /**
   * Get the collection url by collection url or handle, page number and other additional url search parameters
   * @param collectionHandleOrUrl
   * @param page
   * @param searchParameter
   * @returns
   */
  public static getCollectionUrl(
    collectionHandleOrUrl: string,
    page = 1,
    searchParameter?: URLSearchParams,
  ) {
    let _url = collectionHandleOrUrl.includes('/collections/')
      ? normalizeUrl(collectionHandleOrUrl).url
      : `${this.root}collections/${collectionHandleOrUrl}`

    if (!_url?.startsWith('http')) {
      _url = window.location.protocol + '//' + window.location.host + _url
    }

    const url = new URL(_url)

    return setPageAndSearchParameters(url, page, searchParameter)
  }

  private static async _collection(
    collectionHandleOrUrl: string,
    page = 1,
    searchParameter?: URLSearchParams,
  ): Promise<CollectionJsonResponse | null> {
    const url = this.getCollectionUrl(
      collectionHandleOrUrl,
      page,
      searchParameter,
    )
    // Set view=raw to get the json object, see `shopify/layout/raw.liquid` for this
    url.searchParams.set('view', 'json')

    return responseSectionToJson(await HttpService.get<string>(url.toString()))
  }

  /**
   * Load a collection object and it's products by the collection handle or collection url
   * @param collectionHandleOrUrl The collection handle or url to get the collection object from
   * @param page The page number you want to get the products for
   * @param searchParameter Additional url search parameters, used for collection filtering and sorting
   * @returns
   */
  public static async collection(
    collectionHandleOrUrl: string,
    page = 1,
    searchParameter?: URLSearchParams,
  ): Promise<CollectionJsonResponse | null> {
    if (!collectionHandleOrUrl)
      throw new Error('Collection handle or url not set!')
    return pRetry(
      this._collection.bind(this, collectionHandleOrUrl, page, searchParameter),
    )
  }

  /**
   * Check if the option values fits to the current variant.
   * @param variant
   * @param optionValues
   * @return Returns `true` if the option values are fitting to the variant
   */
  public static fitsVariantOptions(
    variant: ShopifyProductVariant,
    optionValues: CardProductOptionValue[],
  ) {
    let fit = true
    // position0 is the option index starting on 0
    for (const i in optionValues) {
      if (optionValues[i]) {
        const optionValue = optionValues[i]
        fit = fit && variant.options.indexOf(optionValue.value.toString()) > -1
      }
    }
    return fit
  }

  public static getRatingTemplates(
    smallClass = 'text-base font-body',
    variantBarcode?: string,
  ) {
    let normal = ''
    let small = ''
    if (variantBarcode) {
      normal = `<etrusted-widget data-etrusted-widget-id="wdg-8238a69d-178e-41f9-ba9c-f3163a57863a" data-gtin="${variantBarcode}" class="block"></etrusted-widget>`
      small = `<etrusted-widget data-etrusted-widget-id="wdg-acb3089b-51d1-4ac0-b583-fc534656c0fa" data-gtin="${variantBarcode}" class="${smallClass}"></etrusted-widget>`
    }
    return { normal, small }
  }

  /**
   * Get product variant of (selected) option values
   * @param optionValues (selected) option values
   */
  public static getVariantOfOptions(
    product: ShopifyProduct,
    optionValues: CardProductOptionValue[],
  ) {
    let result: ShopifyProductVariant | null = null

    if (optionValues.length !== product.options.length) {
      return null
    }
    for (const i in product.variants) {
      if (product.variants[i]) {
        result = null
        const variant = product.variants[i]
        const fits = this.fitsVariantOptions(variant, optionValues)
        if (fits) {
          result = variant
          break
        }
      }
    }

    return result
  }

  /**
   * Get variant object by variant id
   * @param variantId Variant id
   */
  public static getVariant(product: ShopifyProduct, variantId: number) {
    const result =
      product.variants.find(variant => variant.id === variantId) || null
    return result
  }

  /**
   * Get product option by name
   * @param product product which holds the options
   * @param name option name
   */
  public static getOption(product: ShopifyProduct, name: string) {
    const result = product.options.find(
      option => option.name.toLowerCase() === name.toLowerCase(),
    )
    return result
  }

  /**
   * Get product option which includes a substring
   * @param product product which holds the options
   * @param name option name
   */
  public static getOptionIncludes(
    product: ShopifyProduct,
    searchSubstr: string,
  ) {
    const result = product.options.find(option =>
      option.name.includes(searchSubstr),
    )
    return result
  }

  public static normalizeImageObject(
    img:
      | string
      | ShopifyProductImage
      | ShopifyImage
      | ShopifyPreviewImage
      | ShopifyMedia
      | ShopifyCartLineItemFeaturedImage
      | null,
  ) {
    let result: Partial<ShopifyNormalizedImage> = {}

    if (typeof img === 'string') {
      result.src = img
    } else if (typeof img === 'object') {
      if (typeof (img as ShopifyProductImage)?.src === 'string') {
        result = img as ShopifyProductImage
      } else if (
        typeof (img as ShopifyMedia)?.preview_image?.src === 'string'
      ) {
        result = img as ShopifyMedia
        result.src ||= (img as ShopifyMedia)?.preview_image.src
      } else if (
        typeof (img as ShopifyCartLineItemFeaturedImage)?.url === 'string'
      ) {
        result = img as ShopifyCartLineItemFeaturedImage
        result.src ||= (img as ShopifyCartLineItemFeaturedImage).url
      } else {
        result = img as any
      }
    }

    if (!result) {
      console.warn('[normalizeImageObject] result is falsy', result)
    }

    if (typeof result?.src === 'string') {
      // remove protocol
      result.src = result.src.replace(/(^\w+:|^)\/\//, '//')
    }

    return result
  }

  public static getFeaturedImage(
    product: ShopifyProduct | null = null,
    variant: ShopifyProductVariant | null = null,
  ) {
    const image = this.normalizeImageObject(
      variant?.featured_media ||
        variant?.featured_image ||
        product?.featured_image ||
        null,
    )
    return image
  }

  /** @deprecated */
  public static setCurrentFeaturedImage(
    currentImageObject: Partial<ShopifyNormalizedImage>,
    product: ShopifyProduct | null = null,
    variant: ShopifyProductVariant | null = null,
  ) {
    const image = this.getFeaturedImage(product, variant)
    return extend({}, currentImageObject, image)
  }

  /**
   * Set the product background color image
   * @param el The element you want to set the background css variable for
   * @param bgColor background color
   * @returns
   */
  public static setImageBgColor(el: HTMLElement, bgColor?: string) {
    if (!bgColor) return

    if (!isColor(bgColor)) {
      const colorName = handleize(bgColor)
      bgColor = `var(--color-${colorName})`
      // remove all product image color classes
      for (const classItem of Array.from(el.classList)) {
        if (classItem.startsWith('product-image-bg-color-')) {
          el.classList.remove(classItem)
        }
      }
      el.classList.add(`product-image-bg-color-${colorName}`)
    }

    el.style.setProperty('--product-bg-image-color', bgColor)

    el.style.setProperty(
      '--product-bg-image',
      `linear-gradient(
      180deg,
      var(--product-bg-image-color) 75%,
      rgba(255, 255, 255, 0) 75%
    )`,
    )
  }

  public static getFirstAvailableVariant(product: ShopifyProduct) {
    const variants = product?.variants
    if (!variants || !variants.length) {
      console.error(new Error('No variants found!'))
      return
    }

    // Find the first available variant
    for (const variant of variants) {
      if (variant.available) {
        return variant
      }
    }

    // If no variant is available, return the first found variant
    for (const variant of variants) {
      if (variant) {
        return variant
      }
    }

    return undefined
  }

  public static calcSalePercent(
    variant: ShopifyProductVariant | ShopifyProduct,
  ) {
    let sale = 0
    if (!variant) return sale

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      sale = Math.round(
        ((variant.compare_at_price - variant.price) /
          variant.compare_at_price) *
          100,
      )
    }

    return sale
  }

  public static calcSaleSum(data: {
    compare_at_price?: number | null
    price?: number
  }) {
    let sale = 0
    if (!data) return sale

    if (
      data.price &&
      data.compare_at_price &&
      data.compare_at_price > data.price
    )
      sale = data.compare_at_price - data.price
    return sale
  }

  public static getDiscountLabel(variant?: ShopifyProductVariant) {
    return variant?.metafields?.seeberger?.discount_label || ''
  }

  public static prepareBadges(
    product: ShopifyProduct | undefined,
    variant: ShopifyProductVariant | undefined,
    metafields: Metafields | null,
    activeMultipack?: Multipack,
  ) {
    const badges: SbProductBadges = {
      new: false,
      sale: 0,
      saleSum: 0,
      soldOut: !product?.available,
      outOfStockText: '',
      discountInfo: '',
      additionalInfo: '',
    }

    if (!product) return badges

    // Stop here if sold out
    if (badges.soldOut) return badges

    // Sale
    badges.sale =
      this.calcSalePercent(variant || product) ||
      this.getVariantSaleBadgeForMultipack(activeMultipack)

    // Sale sum
    badges.saleSum = this.calcSaleSum(variant || product)

    // Out Of Stock Text
    badges.outOfStockText = metafields?.seeberger?.out_of_stock_text || ''

    // Discount info text
    badges.discountInfo = this.getDiscountLabel(variant)

    // Additional info text
    badges.additionalInfo = metafields?.seeberger?.info_label || ''

    // New
    const marketId = getMarketId()
    const newBadge = this.localizedNewBadgeTags[marketId]
    if (!newBadge) {
      console.warn(`Could not find new badge tag for market ${marketId}`)
    } else {
      badges.new = product.tags.includes(newBadge)
    }

    return badges
  }

  private static async _getMetafields(
    productHandleOrUrl: string,
    enableCache = true,
  ): Promise<Metafields | null> {
    let url = productHandleOrUrl.includes('/products/')
      ? productHandleOrUrl
      : `${this.root}products/${productHandleOrUrl}`

    if (url.includes('?')) {
      url = url + '&view=json-metafields'
    } else {
      url = url + '?view=json-metafields'
    }

    const cacheKey = this.getMetafieldsCacheKey(productHandleOrUrl)

    if (enableCache) {
      const metafields = await this.cache.resolve<Promise<Metafields>>(cacheKey)
      if (metafields) {
        return metafields
      }
    }

    return responseSectionToJson(await HttpService.get<string>(url))
  }

  /**
   * Get metafields for product by handle or url
   * TODO use section rendering api? https://shopify.dev/api/section-rendering
   * @param productHandleOrUrl Product handle or url
   * @returns
   */
  public static async getMetafields(
    productHandleOrUrl: string,
    enableCache = true,
  ): Promise<Metafields | null> {
    return pRetry(
      this._getMetafields.bind(this, productHandleOrUrl, enableCache),
    )
  }

  private static async prepareProduct(
    product: ShopifyProduct,
    optionNamesPackI18n: string[],
    allowedVariantIds?: number[],
  ) {
    if (product.sbIsPrepared) {
      return product
    }

    if (!this.productHasOptionsWithValues(product)) {
      throw new Error('Product options not with values!')
    }

    // Prepare title
    product.title = product.title.replaceAll('`', "'")

    // Prepare url
    if (!product.url) {
      product.url = '/products/' + product.handle
    }

    product = this.cleanIrrelevantVariantsAndOptions(product, allowedVariantIds)
    product.options = this.prepareOptions(product, optionNamesPackI18n)
    product.sbImagesDetails = await this.prepareImages(product)
    product.sbIsPrepared = true

    // Update cache for prepared product
    // return this.cacheProduct(product)

    return product
  }

  /**
   * Get prepared product data including metafields, badges, featured image, variant, etc.
   * @param productHandleOrUrl Product handle or url
   * @param variantId Variant id
   * @param enableCache
   * @returns
   */
  public static async getPreparedProductData(
    productHandleOrUrl: string,
    variantId: number | undefined,
    enableCache = true,
    allowedVariantIds?: number[] | undefined,
  ) {
    let product = await this.getSingle(productHandleOrUrl, enableCache)

    let metafields = await this.getMetafields(productHandleOrUrl, enableCache)
    let bgColor = ''
    let variant: ShopifyProductVariant | undefined = undefined

    if (metafields) {
      const { bgColor: bgColorFromMetafields, metafields: preparedMetafields } =
        this.prepareMetafields(metafields, product)
      metafields = preparedMetafields
      bgColor = bgColorFromMetafields
    }

    const optionNamesPackI18n =
      await ShopifyProductService.getOptionNames('pack')

    product = await this.prepareProduct(
      product,
      optionNamesPackI18n,
      allowedVariantIds,
    )

    if (variantId) {
      variant = ShopifyProductService.getVariantById(product, variantId)
      if (!variant) {
        console.warn(new Error(`No variant found for id ${variantId}`))
      }
    }

    return {
      product,
      metafields,
      bgColor,
      variant,
    }
  }

  public static async getPreparedProductVariantData(
    product: ShopifyProduct,
    variant: ShopifyProductVariant,
    metafields: Metafields | null,
    activeMultipack: Multipack = DEFAULT_MULTIPACK,
  ) {
    const multipacks = this.getMultipackData(variant)

    const featuredImage = this.getFeaturedImage(product, variant)
    const badges = this.prepareBadges(product, variant, metafields)

    const colliLabel = await this.getVariantColliLabelForMultipack(
      variant,
      activeMultipack,
    )

    this.setVariantOrderLimitFromMetafields(variant)

    return {
      badges,
      featuredImage,
      multipacks,
      colliLabel,
    }
  }

  /**
   * Returns the multipack discounts object
   *
   * @example
   * {
   *   "A_3": { quantity: 3, percentage: 5 },
   *   "B_6": { quantity: 6, percentage: 3 }
   * }
   */
  private static getMultipackDiscountFromMetafield() {
    return window.multipackDiscounts
  }

  /**
   * Extracts and transforms multipack data from a product variant
   *
   * @param variant - The Shopify product variant to extract multipack data from
   *
   * @returns An array of Multipack objects containing quantity and discount percentage
   *
   * @example
   * // Input variant.metafields.seeberger.multipack: ["A_3", "B_6"]
   * // With window.multipackDiscounts:
   * // {
   * //   "A_3": { quantity: 3, percentage: 5 },
   * //   "B_6": { quantity: 6, percentage: 3 }
   * // }
   * // Returns:
   * [
   *   { quantity: 1, percentage: 0 }, // DEFAULT_MULTIPACK
   *   { quantity: 3, percentage: 5 },
   *   { quantity: 6, percentage: 3 }
   * ]
   *
   * @remarks
   * - Uses multipack discount data defined in `shopify/snippets/utils-global-javascript.liquid`
   * - Always includes DEFAULT_MULTIPACK as first entry
   */
  private static getMultipackData(variant: ShopifyProductVariant) {
    const multipack: Multipack[] = [DEFAULT_MULTIPACK]
    const multipackDiscounts = this.getMultipackDiscountFromMetafield()
    if (variant && variant.metafields?.seeberger?.multipack?.length) {
      for (const multipackKey of variant.metafields.seeberger.multipack) {
        const discountInfo = multipackDiscounts?.[multipackKey]
        // If the multipack discount is not defined, skip it
        if (!discountInfo) {
          continue
        }
        multipack.push({
          quantity: discountInfo.quantity,
          percentage: discountInfo.percentage || 0,
        })
      }
    }

    return multipack
  }

  protected static getVariantSaleBadgeForMultipack(multipack?: Multipack) {
    const percentage = multipack?.percentage || 0
    const sale = percentage * -1
    return sale
  }

  /**
   * Calculates the discounted prices for a variant based on multipack percentage
   *
   * @param variant - The Shopify product variant
   * @param multipack - Optional multipack configuration with discount percentage
   * @returns Object containing the calculated new price
   */
  protected static getVariantPricesForMultipack(
    variant: ShopifyProductVariant,
    multipack?: Multipack,
  ) {
    const percentage = multipack?.percentage || 0
    const price = variant.price
    if (!price) {
      throw new Error('Price is not set!')
    }

    const discountMultiplier = 1 - percentage / 100

    return {
      newPrice: price * discountMultiplier,
      newUnitPrice:
        variant.unit_price !== null
          ? variant.unit_price * discountMultiplier
          : null,
    }
  }

  protected static async getVariantColliLabelForMultipack(
    variant: ShopifyProductVariant,
    multipack?: Multipack,
  ) {
    const count = multipack?.quantity || 1
    let colliLabel = ''
    if (count > 1) {
      const localeService = SbLocaleService.getSingleton()
      colliLabel = await localeService._(
        'products.product.colli_label.default_html',
        { count: count.toString() },
      )
    } else {
      colliLabel = variant?.metafields?.seeberger?.colli_label || ''
    }
    return colliLabel
  }

  /**
   * Get variant by id
   * @param product The product object
   * @param id The variant id
   * @returns The variant object or undefined if not found
   */
  public static getVariantById(
    product: ShopifyProduct,
    id: number | string,
  ): ShopifyProductVariant | undefined {
    if (!product) return
    return product.variants.find(
      variant => variant.id.toString() === id.toString(),
    )
  }

  /**
   * Prepare metafields for product
   * @param metafields The metafields object
   * @param product The product object
   * @returns The prepared metafields object
   */
  public static prepareMetafields(
    metafields: Metafields,
    product: ShopifyProduct,
  ) {
    const bgColor = ShopifyProductService.getBgColorFromMetafields(metafields)

    // Update variant metafields
    if (metafields.variants) {
      const variantIds = Object.keys(metafields.variants)
      for (const variantId of variantIds) {
        const variant = this.getVariantById(product, variantId)
        if (!variant || !metafields.variants[variantId]) continue

        variant.metafields = metafields.variants[variantId]
      }
    }

    return {
      bgColor,
      metafields,
    }
  }

  /**
   * Get the translated names of the option.
   * Only used internally to check if the option name is "pack" in it's translated form.
   * Sometimes the english translation is used instead of the current language translation, so we need to check all translations.
   * @param option
   * @returns
   */
  public static async getOptionNames(optionName: string) {
    const localeService = SbLocaleService.getSingleton()
    const packTranslations: string[] = []
    const path = ['products', 'product', 'option_name']
    const currentLangCode = localeService.getLangcode() ?? 'de'

    // All languages with the current one on the beginning
    const langCodes: string[] = ['en', 'de', 'fr'].filter(
      code => code !== currentLangCode,
    )
    langCodes.unshift(currentLangCode)

    for (const langCode of langCodes) {
      const pack = await localeService.get([langCode, ...path, optionName])
      if (pack) {
        packTranslations.push(pack)
      }
    }

    return packTranslations
  }

  private static prepareOptions(
    product: ShopifyProduct,
    optionNamesPackI18n: string[],
  ) {
    // all option names to lower case and copy to custom options array
    for (const option of product.options) {
      // Remove "Default Title" Option values
      option.values = this.renameOptionValues(option.values)

      // Sort option values, currently disabled
      // option = this.sortOption(option)
    }

    // Sort product options, to move "pack" options to the end
    product.options = product.options.sort((a, b) => {
      const aIsPack = optionNamesPackI18n.some(
        packName => packName.toLowerCase() === a.name.toLowerCase(),
      )
      const bIsPack = optionNamesPackI18n.some(
        packName => packName.toLowerCase() === b.name.toLowerCase(),
      )

      if (aIsPack && !bIsPack) return 1
      if (!aIsPack && bIsPack) return -1
      return 0
    })

    for (const variant of product.variants) {
      if (variant.options) {
        variant.options = this.renameOptionValues(variant.options)
      }
      if (variant.option1) {
        variant.option1 = this.renameOptionValue(variant.option1)
      }
      if (variant.option2) {
        variant.option2 = this.renameOptionValue(variant.option2)
      }
      if (variant.option3) {
        variant.option3 = this.renameOptionValue(variant.option3)
      }
    }

    return product.options
  }

  private static async prepareImages(product: ShopifyProduct) {
    product.sbImagesDetails = await this.extractImagesDetails(product)
    return product.sbImagesDetails
  }

  /**
   * Generate regex for `variant` options with or without position
   * @see https://docs.google.com/document/d/1Rd8zu_WIXheJ6CQFzfo7-X_nieCXgJ-iLNSW8zzF9KQ/edit#heading=h.5cesr9czsk3s
   * @param variant
   * @returns
   */
  private static getVariantOptionImageRegex(
    variant: ShopifyProductVariant,
    packOptionNames: string[],
  ): ProductVariantOptionImageRegex {
    let packOption = 0
    let searchPatterns: string[] = []
    /** E.g. `1x150g` */
    const notPackOptions: string[] = []
    const lowerCaseOptions = variant.options.map(option => option.toLowerCase())

    for (const option of lowerCaseOptions) {
      if (packOptionNames.some(name => option.includes(name))) {
        packOption = extractNumber(option) || 0
      } else {
        notPackOptions.push(option)
      }
    }

    // Special case for packs:
    // @deprecated
    // - pack option 1: 1x150g
    // - pack option 2: 2x150g
    // - pack option 3: 3x150g
    if (packOption && packOption > 0) {
      searchPatterns.push(
        `${packOption}x${notPackOptions.map(option => option.replace(/\s+/g, '[-_]?')).join('-')}`,
      )
    }

    // E.g. `1000[-_]?g-ganze[-_]?bohne`
    searchPatterns.push(
      lowerCaseOptions.map(option => option.replace(/\s+/g, '[-_]?')).join('-'),
    )

    // E.g. `ganze[-_]?bohne-1000[-_]?g`
    searchPatterns.push(
      lowerCaseOptions
        .reverse()
        .map(option => option.replace(/\s+/g, '[-_]?'))
        .join('-'),
    )

    searchPatterns = searchPatterns.sort((a, b) => b.length - a.length)

    const patterns: ProductVariantOptionImagePattern[] = searchPatterns.map(
      pattern => ({
        regex: new RegExp(`[-_](${pattern})[-_.]`, 'i'),
        regexWithIndex: new RegExp(`[-_](${pattern})-(\\d+)`, 'i'),
      }),
    )

    return {
      patterns,
      variantId: variant!.id,
      variant,
    }
  }

  /**
   * Generate regex for all variants options with or without position
   * @returns
   */
  private static async getVariantsOptionImageRegex(product: ShopifyProduct) {
    const result: ProductVariantOptionImageRegex[] = []

    const packOptionNames = (
      await ShopifyProductService.getOptionNames('pack')
    ).map(option => option.toLowerCase())

    for (const variant of product?.variants) {
      result.push(this.getVariantOptionImageRegex(variant, packOptionNames))
    }

    // Order result by pattern length, longer patterns should be searched for first, otherwise the results may be incorrect
    return result.sort((a, b) => b.patterns.length - a.patterns.length)
  }

  /**
   * Get the variant which has the image linked by shopify (the normal behavior, not that using the filename)
   * @param image
   */
  private static getShopifyVariantByImage(
    product: ShopifyProduct,
    image: ShopifyMediaImage,
  ) {
    if (!product?.variants) return null

    for (const variant of product?.variants) {
      if (variant.featured_media?.id === image.id) {
        return variant
      }
      if (variant.featured_image?.id === image.id) {
        return variant
      }
    }

    return null
  }

  /**
   * Extract `variantId` and `position` from image's filename.
   * For the rule definition, see [Dokumentation Shopify Pflege](https://docs.google.com/document/d/1Rd8zu_WIXheJ6CQFzfo7-X_nieCXgJ-iLNSW8zzF9KQ/edit#heading=h.lpivyr990bne)
   * @param image
   * @param variantsRegex
   * @returns
   */
  private static getVariantForImage(
    product: ShopifyProduct,
    image: ShopifyMediaImage,
    variantsRegex: ProductVariantOptionImageRegex[],
  ) {
    // Find matching image filename for variant
    for (const regex of variantsRegex) {
      for (const pattern of regex.patterns) {
        if (image.src.match(pattern.regex)) {
          const [, , position] = image.src.match(pattern.regexWithIndex) || []
          if (regex.variantId !== 0) {
            return {
              variantId: regex.variantId,
              variant: regex.variant,
              position: position ? parseInt(position) || 1 : 1,
            }
          }
        }
      }
    }

    // As a fallback use the image linked to the variant by shopify (not using the filename)
    const fallbackVariant = this.getShopifyVariantByImage(product, image)
    const [, , position] =
      image.src.match(
        ANY_VARIANT_OPTION_IMAGE_REGEX_WITH_INDEX.regexWithIndex,
      ) || []

    if (fallbackVariant) {
      return {
        variantId: fallbackVariant.id,
        variant: fallbackVariant,
        position: position ? parseInt(position) || 1 : 1,
      }
    }

    return {
      variantId: 0,
      variant: null,
      position: position ? parseInt(position) || 1 : 1,
    }
  }

  /**
   * Extract product (variant) image details by it's filename.
   * For the rule definition, see [Dokumentation Shopify Pflege](https://docs.google.com/document/d/1Rd8zu_WIXheJ6CQFzfo7-X_nieCXgJ-iLNSW8zzF9KQ/edit#heading=h.lpivyr990bne)
   */
  private static extractImageDetails(
    product: ShopifyProduct,
    image: ShopifyMediaImage,
    variantsRegex: ProductVariantOptionImageRegex[],
  ) {
    const src = image.src

    let bgfull = false
    let borderless = false
    let border = false

    if (src.includes('borderless')) {
      borderless = true
    } else if (src.includes('border') || src.includes('freisteller')) {
      border = true
    }
    if (src.includes('bgfull')) {
      bgfull = true
    }

    const markets: string[] = this.getMarketsFromImage(image.src)
    const multipack = this.getMultipackFromImage(image.src)

    const moreDetails = this.getVariantForImage(product, image, variantsRegex)

    // Set fallback alt text
    if (!image.alt && product && moreDetails.variant) {
      // title + variant title + position
      image.alt = `${product.title} - ${moreDetails.variant.title} - ${moreDetails.position || 1}`
    }

    const result: SbProductVariantImageDetails = {
      markets,
      multipack,
      bgfull,
      borderless,
      border,
      media: image,
      loadedFull: false,
      loadedPreview: false,
      loadedThumbnail: false,
      ...moreDetails,
    }
    return result
  }

  /**
   * Extract markets from image src.
   * Note: The market in the filename must be uppercase but is optional.
   * @param imageSrc
   * @returns
   */
  private static getMarketsFromImage(imageSrc: string): string[] {
    const markets = MARKETS.filter(market => imageSrc.includes(market))
    return markets
  }

  private static getMultipackFromImage(imageSrc: string): number | undefined {
    const match = imageSrc.match(/[_-]([0-9]+)er-pack[-_.]/)?.[1]

    return match ? parseInt(match, 10) : undefined
  }

  /**
   * Extract product (variant) image details for all images and group them by variant
   */
  private static async extractImagesDetails(
    product: ShopifyProduct,
  ): Promise<SbProductVariantImageDetailsGrouped> {
    const variantsRegex = await this.getVariantsOptionImageRegex(product)

    const variantImagesArray: SbProductVariantImageDetails[] = []

    for (const image of product.media) {
      const imageDetails = this.extractImageDetails(
        product,
        image,
        variantsRegex,
      )
      // Skip image if it's not for the current market
      if (
        // If the image has no markets, it's valid for all markets
        imageDetails.markets.length === 0 ||
        // If the image has the market "ALL", it's valid for all markets
        imageDetails.markets.includes('ALL') ||
        // If the image has the current market, it's valid for the current market
        imageDetails.markets.includes(getMarketId())
      ) {
        variantImagesArray.push(imageDetails)
      }
    }

    /** Image details grouped by variant */
    const grouped: SbProductVariantImageDetailsGrouped = {
      /** Image details for all variants */
      0: [],
    }

    // Init grouped for all variant ids
    for (const variant of product.variants) {
      grouped[variant.id] = []
    }

    // Group images by variant id
    for (const variantImage of variantImagesArray) {
      const id = variantImage.variantId
      grouped[id] = grouped[id] || []
      grouped[id].push(variantImage)
    }

    // Add all images of the 0 group  (which represents all variants) to the variant id groups to display them on all variants
    for (const id of Object.keys(grouped)) {
      const variantId = Number(id)
      if (variantId === 0) {
        continue
      }

      if (!variantId) continue
      grouped[variantId] = grouped[variantId] || []
      grouped[variantId].push(...(grouped[0] || []))
    }

    // Sort group by position
    for (const id in grouped) {
      if (grouped[id]) grouped[id].sort((a, b) => a.position - b.position)
    }

    // Check if a variant has no images and use the variant images of the first variant as a FALLBACK
    for (const _variantId of Object.keys(grouped)) {
      const variantId = Number(_variantId)
      if (!variantId) continue
      if (!grouped[variantId].length) {
        const fallbackVariantId = this.getFirstVariantWithImages(grouped)
        if (fallbackVariantId) {
          grouped[variantId] = grouped[fallbackVariantId]
        }

        if (window.debug) {
          console.warn(
            `The variant with id "${variantId}" has no images, using the images of the first variant as a fallback`,
            fallbackVariantId,
            grouped,
          )
        }

        if (!fallbackVariantId) {
          console.warn(
            `No fallback variant found for variant with id "${variantId}"`,
          )
        }
      }
    }

    // DEBUG: Check if some images couldn't be assigned to any group
    // product.media.forEach(image => {
    //   if (
    //     !Object.values(grouped).some(
    //       (images: SbProductVariantImageDetailsGrouped[number]) => {
    //         return images.some(
    //           assignedImage => assignedImage.media.id === image.id,
    //         )
    //       },
    //     )
    //   ) {
    //     console.warn(
    //       `Could not assign image "${image.id} / ${image.src}" to any variant`,
    //     )
    //   }
    // })

    return grouped
  }

  private static getFirstVariantWithImages(
    grouped: SbProductVariantImageDetailsGrouped,
  ) {
    for (const _variantId in grouped) {
      const variantId = Number(_variantId)
      if (variantId === 0) continue
      if (grouped[variantId].length) return variantId
    }
  }

  /**
   * Check if the variant is hidden on the current market
   * @param variantMetafields The variant metafields
   * @param marketCountryCode The current market country code
   * @returns `true` if the variant is hidden on the current market, `false` otherwise
   */
  public static hiddenOnMarket(
    variantMetafields: MetafieldsProductVariant | null | undefined,
  ) {
    if (!variantMetafields?.seeberger) {
      throw new Error(
        'variantMetafields.seeberger is undefined! Did you called `prepareMetafields`?',
      )
    }
    // seeberger property is set but market allowlist (`seeberger.market_whitelist`) is not set, than the variant is allowed for all markets
    if (!variantMetafields.seeberger.market_whitelist?.length) {
      return false
    }

    // Otherwise the variant is white listed for the current market
    const onAllowList =
      variantMetafields?.seeberger?.market_whitelist?.includes(getMarketId()) ||
      false

    return !onAllowList
  }

  /**
   * Check whether to show the separate section "Inhalt". This is used for products without an "Inhalt" option that have it set as metafield.
   * @param variant The product variant to check
   * @returns
   */
  public static hasPackageContentsFallback(
    variant: ShopifyProductVariant,
  ): boolean {
    if (!variant?.metafields?.seeberger.package_contents) {
      return false
    }

    return true
  }

  /**
   * Get the translated package contents label.
   * @param label If no label is set, the default translation path is used.
   * @returns The translated package contents label.
   */
  public static async getPackageContentsLabel(label?: string) {
    if (label) {
      return label
    }
    const localeService = SbLocaleService.getSingleton()
    return await localeService._(
      'products.product.option_name.package_contents',
    )
  }

  public static getBgColorFromMetafields(
    metafields: Metafields | null,
  ): string {
    return metafields?.seeberger?.bg_color || ''
  }

  /**
   * Set the quantity order limit from the metafields for a variant.
   * @param variant
   * @returns The quantity order limit
   */
  public static setVariantOrderLimitFromMetafields(
    variant: ShopifyProductVariant,
  ): void {
    variant.quantity_rule ??= {
      min: 1,
      max: null,
      increment: 1,
    }
    variant.quantity_rule.max ??=
      variant.metafields?.seeberger?.order_limit || null
  }

  /**
   * Split options to `values` (max 3), more (the other more than 3) and all.
   * @param option
   * @returns
   */
  public static splitOption(
    option: ShopifyProductVariantOption,
    autoSelect = false,
  ) {
    const values = option.values.map(value => {
      return {
        value,
        active: false,
        autoSelect,
        available: undefined,
        exists: true,
        hidden: false,
      }
    })

    const transOption: CardProductOption = {
      name: option.name,
      position: option.position,
      autoSelect,
      values: [],
      more: [],
      all: [],
    }

    // Split options to three and more
    if (values.length > 3) {
      const three = values.slice(0, 3)
      const more = values.slice(3)
      transOption.values = three
      transOption.more = more
      transOption.all = [...three, ...more]
    } else {
      transOption.values = values
      transOption.all = transOption.values
    }
    return transOption
  }

  /**
   * The default shopify product json object returns the options as a array of option names, but the json request returns an array of option objects.
   * This method checks if the options array is an array of objects
   * @param product
   * @returns
   */
  public static productHasOptionsWithValues(product: ShopifyProduct) {
    if (!product.options.length) {
      return true
    }
    const exampleOption = product.options[0]
    if (
      exampleOption.hasOwnProperty('name') &&
      exampleOption.hasOwnProperty('values') &&
      exampleOption.hasOwnProperty('position')
    ) {
      return true
    }
    return false
  }

  /**
   * Sort option values: 500 g, 1000 g, 250g -> 250g, 500 g, 1000 g
   * @param option
   * @returns
   */
  public static sortOption(option: ShopifyProductVariantOption) {
    option.values = option.values.sort((a, b) => {
      const aNum = justDigits(a)
      const bNum = justDigits(b)
      if (aNum !== 0 && bNum !== 0) {
        return aNum - bNum
      }

      return a.localeCompare(b)
    })
    return option
  }

  /**
   * Replaces [...] "Default Title" option value
   * @param option
   * @returns
   */
  public static renameOptionValue(optionValue: string) {
    // Remove (...)
    optionValue = optionValue.replace(/\([^)]*\)/g, '')
    // Remove [...]
    optionValue = optionValue.replace(/\[[^\]]*\]/g, '')
    // Trim
    optionValue = optionValue.trim()
    // Remove Default Title
    if (optionValue === 'Default Title') {
      optionValue = ''
    }
    return optionValue
  }

  /**
   * Replaces "Default Title" option values
   * @param option
   * @returns
   */
  public static renameOptionValues(optionValues: string[]) {
    for (let i = 0; i < optionValues.length; i++) {
      optionValues[i] = this.renameOptionValue(optionValues[i])
    }
    return optionValues
  }

  /** Test if two product or variant objects are equal */
  public static equal(
    p1?: ShopifyProduct | ShopifyProductVariant,
    p2?: ShopifyProduct | ShopifyProductVariant,
  ) {
    if (p1 === p2) {
      return true
    }

    if (!p1 || !p2) {
      return p1 === p2
    }

    if (p1.id !== p2.id) {
      return false
    }

    if (
      typeof p1.updated_at === 'object' &&
      typeof p2.updated_at === 'object' &&
      p1.updated_at?.getTime() !== p2.updated_at?.getTime()
    ) {
      return false
    } else if (p1.updated_at !== p2.updated_at) {
      return false
    }

    return true
  }

  /**
   * Get the maximum available inventory quantity for a variant.
   * @param variant The product variant.
   * @returns Maximum available quantity or Infinity if inventory tracking is disabled.
   */
  public static getMaxAvailableInventoryQuantity(
    variant: ShopifyProductVariant,
  ): number {
    // If inventory is not tracked, allow any quantity
    if (!variant.inventory_management) {
      console.debug('inventory is not tracked, allow any quantity')
      return Infinity
    }

    // If inventory policy is 'continue', allow any quantity
    if (variant.inventory_policy === 'continue') {
      console.debug('inventory policy is continue, allow any quantity')
      return Infinity
    }

    // Return the inventory quantity if it is a number, otherwise return Infinity
    if (typeof variant.inventory_quantity === 'number') {
      let inventoryQuantity = variant.inventory_quantity
      if (inventoryQuantity < 0) {
        inventoryQuantity = 0
      }
      return inventoryQuantity
    }
    console.warn('inventory quantity is not a number', variant)
    return Infinity
  }

  /**
   * Get the quantity rule for a variant.
   * @param variant The product variant.
   * @returns The quantity rule with max, increment, and min values.
   */
  public static getQuantityRule(
    variant: ShopifyProductVariant,
  ): ShopifyProductVariantQuantityRule & { max: number } {
    const max = variant.quantity_rule?.max ?? Infinity
    const increment = variant.quantity_rule?.increment ?? 1
    const min = variant.quantity_rule?.min ?? 1

    if (max < min) {
      throw new Error('Max is less than min')
    }

    return { max, increment, min }
  }

  /**
   * Gets the maximum available quantity based on inventory and max rules
   */
  public static getMaxAvailableQuantity(
    variant: ShopifyProductVariant,
  ): number {
    const { max } = this.getQuantityRule(variant)
    return Math.min(max, this.getMaxAvailableInventoryQuantity(variant))
  }

  /**
   * Checks if the requested quantity is within allowed limits
   */
  public static isQuantityWithinLimits(
    nextQuantity: number,
    maxAvailable: number,
  ): boolean {
    return nextQuantity <= maxAvailable
  }

  /**
   * Shows error message when quantity limit is reached
   */
  public static async showQuantityValidationError(
    result: ValidateQuantityResult,
  ): Promise<void> {
    let i18nKey = ''

    if (result.isValid) {
      throw new Error('Validation result is valid')
    }

    if (result.validationError === VALIDATE_QUANTITY_ERROR.LessThanMin) {
      i18nKey = 'products.product.quantity.minimum_quantity_error'
    } else {
      if (result.isMultipack) {
        if (result.cartItemsCount > 0) {
          i18nKey =
            'products.product.quantity.limit_reached_with_cart_items_multipack_error'
        } else {
          i18nKey = 'products.product.quantity.limit_reached_multipack_error'
        }
      } else {
        i18nKey = 'products.product.quantity.limit_reached_error'
        if (result.cartItemsCount > 0) {
          i18nKey =
            'products.product.quantity.limit_reached_with_cart_items_error'
        }
      }
    }

    await ToasterService.getSingleton().dangerI18n(i18nKey, result)
  }

  public static getSellingPlans(
    product: ShopifyProduct,
    variant: ShopifyProductVariant,
  ) {
    const plans: ShopifySellingPlan[] = []

    variant.selling_plan_allocations?.forEach(allocation => {
      if (allocation.selling_plan) {
        plans.push(allocation.selling_plan)
        return
      }

      if (!allocation.selling_plan_id) {
        return
      }

      const plan = product.selling_plan_groups
        ?.find(group => group.id === allocation.selling_plan_group_id)
        ?.selling_plans?.find(plan => plan.id === allocation.selling_plan_id)

      if (plan) {
        plans.push(plan)
      }
    })

    return plans
  }
}
